import type { AnyClock } from "@repo/lib/clock";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import {
  RepoCache,
  type GetObservableOptions,
} from "@repo/shared-client/repoCache";
import {
  CreateAgendaItemDtoSchema,
  PatchAgendaItemDtoSchema,
  type AgendaItem,
  type Meeting,
} from "@repo/types";
import {
  cacheDefaults,
  hookPriorities,
  messageServiceSubscribe,
  SubscriptionMap,
} from "./common";
import { apiService } from "@/services/apiService";
import { of, type Observable } from "rxjs";
import { nullObservable } from "@repo/shared-client/commonObservables";
import type { OnlyRequire } from "@repo/lib/types";
import { documentRepo, type DocumentDto } from "./documentRepo";
import { meetingRepo, type MeetingDto } from "./meetingRepo";
import { RepoMutationHooks } from "./repoMutationHooks";
import { baseRepo } from "./baseRepo";

type AgendaItemGetKey = { type: "get"; tenantId: string; agendaItemId: string };
type AgendaItemGetVersionKey = {
  type: "getVersion";
  tenantId: string;
  agendaItemId: string;
  version: number;
};
type AgendaItemListKey = { type: "list"; tenantId: string };
type AgendaItemKey =
  | AgendaItemGetKey
  | AgendaItemGetVersionKey
  | AgendaItemListKey;

export type AgendaItemDto = AgendaItem & {
  tenantId: string;
  documents: DocumentDto[];
};

export type AgendaItemIdPath = {
  tenantId: string;
  agendaItemId: string | null;
};

export class AgendaItemRepository {
  private cache: RepoCache<string, AgendaItemDto, ObjectKeyHasher, AnyClock>;
  private hooks = new RepoMutationHooks<AgendaItemIdPath, AgendaItemDto>();
  private subscribeMap = new SubscriptionMap();

  // expose hook registry
  addHook = this.hooks.addHook;
  removeHook = this.hooks.removeHook;

  submitNewer(agendaItem: AgendaItemDto) {
    this.cache.publish(agendaItem.id, agendaItem);
  }

  agendaItemToDto(tenantId: string, agendaItem: AgendaItem): AgendaItemDto;
  agendaItemToDto(tenantId: string, agendaItem?: null): null;
  agendaItemToDto(
    tenantId: string,
    agendaItem?: AgendaItem | null
  ): AgendaItemDto | null;
  agendaItemToDto(
    tenantId: string,
    agendaItem?: AgendaItem | null
  ): AgendaItemDto | null {
    const documents =
      agendaItem?.documents?.map((document) =>
        documentRepo.documentToDto(tenantId, document)
      ) ?? [];
    return agendaItem ? { ...agendaItem, tenantId, documents } : null;
  }

  constructor() {
    this.cache = new RepoCache({
      requestKeyHasher: new ObjectKeyHasher({ sortKeys: false }),
      expiresAfterSeconds: cacheDefaults.expiresAfterSeconds,
      purgeExpirationAfterSeconds: cacheDefaults.purgeExpiredAfterSeconds,
      fetch: async ({ requestKey, publish }) => {
        const key = requestKey as AgendaItemKey;

        switch (key.type) {
          case "get": {
            const { tenantId, agendaItemId } = key;
            const agendaItem = (
              await apiService.v1.getAgendaItem(tenantId, agendaItemId)
            ).data;

            // only publish if it's the right tenant
            const prev = this.cache.table.get(agendaItemId);
            if (!prev || prev.tenantId === tenantId) {
              publish(agendaItemId, this.agendaItemToDto(tenantId, agendaItem));
            }
            break;
          }
          case "getVersion": {
            const { tenantId, agendaItemId, version } = key;
            const agendaItem = (
              await apiService.v1.getAgendaItemVersion(
                tenantId,
                agendaItemId,
                "" + version
              )
            ).data;
            const strKey = key + ":" + version;

            // only publish if it's the right tenant
            const prev = this.cache.table.get(strKey);
            if (!prev || prev.tenantId === tenantId) {
              publish(strKey, this.agendaItemToDto(tenantId, agendaItem));
            }
            break;
          }
          case "list": {
            const { tenantId } = key;
            const agendaItems = (await apiService.v1.listAgendaItems(tenantId))
              .data;
            for (const agendaItem of agendaItems) {
              publish(
                agendaItem.id,
                this.agendaItemToDto(tenantId, agendaItem)
              );
            }
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const key = requestKey as AgendaItemKey;

        switch (key.type) {
          case "get": {
            return table.get(key.agendaItemId) ?? null;
          }
          case "getVersion": {
            return table.get(key.agendaItemId + ":" + key.version) ?? null;
          }
          case "list": {
            const agendaItems: AgendaItemDto[] = [];
            for (const [strKey, agendaItem] of table) {
              if (
                !strKey.includes(":") &&
                agendaItem.tenantId === key.tenantId
              ) {
                agendaItems.push(agendaItem);
              }
            }
            return agendaItems;
          }
        }
      },
      affectedKeys: ({ entryData, affectsKey }) => {
        // extract
        const { tenantId, id, _version } = entryData;
        // affect
        affectsKey(this.getKey(tenantId, id));
        affectsKey(this.getVersionKey(tenantId, id, _version));
        affectsKey(this.listKey(tenantId));
      },
      initialSubscribe: ({ strKey, requestKey }) => {
        const key = requestKey as AgendaItemKey;
        if (key.type !== "get" && key.type !== "list") return;

        const subscription = messageServiceSubscribe(
          "changing.agendaItems",
          key.type === "get" ? [key.agendaItemId] : [],
          () => this.cache.fetch(key)
        );

        this.subscribeMap.addSubscription(strKey, subscription);
      },
      subscriptionPublished: ({ strKey, requestKey, collectedData }) => {
        const key = requestKey as AgendaItemKey;

        switch (key.type) {
          case "get": {
            const data = collectedData as AgendaItemDto | null;
            if (!data) {
              this.subscribeMap.unsubscribe(strKey);
              break;
            }

            this.subscribeMap.updateChildren({
              strKey,
              idArray: (data.documentIds ??= []),
              objectArray: (data.documents ??= []),
              subscribeChild: (id) =>
                documentRepo.get(key.tenantId, id, { initialFetch: false }),
              publishParent: (idArray, objectArray) => {
                data.documentIds = idArray;
                data.documents = objectArray;
                this.cache.publish(key.agendaItemId, data);
              },
            });
            break;
          }
        }
      },
      finalUnsubscribe: ({ strKey }) => {
        this.subscribeMap.unsubscribe(strKey);
      },
    });

    baseRepo.addRepo(this);
  }

  private getKey(tenantId: string, agendaItemId: string): AgendaItemGetKey {
    return { type: "get", tenantId, agendaItemId };
  }
  private getVersionKey(
    tenantId: string,
    agendaItemId: string,
    version: number
  ): AgendaItemGetVersionKey {
    return { type: "getVersion", tenantId, agendaItemId, version };
  }
  private listKey(tenantId: string): AgendaItemListKey {
    return { type: "list", tenantId };
  }

  get(
    tenantId: string,
    agendaItemId: string,
    options?: GetObservableOptions
  ): Observable<AgendaItemDto | null> {
    if (!tenantId || !agendaItemId) {
      return nullObservable;
    }
    const key = this.getKey(tenantId, agendaItemId);
    return this.cache.observable(key, options);
  }
  getCached(tenantId: string, agendaItemId: string): AgendaItemDto | null {
    const key = this.getKey(tenantId, agendaItemId);
    return this.cache.collectIfUnexpired(key, null);
  }

  getVersion(
    tenantId: string,
    agendaItemId: string,
    version: number,
    options?: GetObservableOptions
  ): Observable<AgendaItemDto | null> {
    if (!tenantId || !agendaItemId || version < 0) {
      return nullObservable;
    }
    const key = this.getVersionKey(tenantId, agendaItemId, version);
    return this.cache.observable(key, options);
  }
  getVersionCached(
    tenantId: string,
    agendaItemId: string,
    version: number
  ): AgendaItemDto | null {
    const key = this.getVersionKey(tenantId, agendaItemId, version);
    return this.cache.collectIfUnexpired(key, null);
  }

  list(
    tenantId: string,
    options?: GetObservableOptions
  ): Observable<AgendaItemDto[]> {
    if (!tenantId) {
      return of([]);
    }
    const key = this.listKey(tenantId);
    return this.cache.observable(key, options);
  }
  listCached(tenantId: string): AgendaItemDto[] | null {
    const key = this.listKey(tenantId);
    return this.cache.collectIfUnexpired(key, null);
  }

  async create(
    tenantId: string,
    data: Omit<Partial<AgendaItemDto>, "documents">
  ): Promise<AgendaItemDto> {
    if (!tenantId) {
      throw new Error("Failed to create agenda item: tenantId is blank.");
    }

    const hooks = this.hooks.execute(
      { tenantId, agendaItemId: null },
      "create"
    );
    data = await hooks.before(data);

    let created = this.agendaItemToDto(
      tenantId,
      (
        await apiService.v1.createAgendaItem(
          tenantId,
          CreateAgendaItemDtoSchema.parse(data)
        )
      ).data
    );

    created = await hooks.after(created);

    // publish new item
    this.cache.publish(created.id, created);

    return created;
  }

  async update(
    tenantId: string,
    agendaItemId: string,
    data: Omit<Partial<AgendaItemDto>, "documents">
  ): Promise<AgendaItemDto> {
    if (!tenantId || !agendaItemId) {
      throw new Error(
        "Failed to update agenda item: tenantId or agendaItemId is blank."
      );
    }

    const hooks = this.hooks.execute({ tenantId, agendaItemId }, "update");
    data = await hooks.before(data);

    let updated = this.agendaItemToDto(
      tenantId,
      (
        await apiService.v1.updateAgendaItem(
          tenantId,
          agendaItemId,
          PatchAgendaItemDtoSchema.parse(data)
        )
      ).data
    );

    updated = await hooks.after(updated);

    // publish updated item
    this.cache.publish(agendaItemId, updated);

    return updated;
  }

  async delete(tenantId: string, agendaItemId: string): Promise<void> {
    if (!tenantId || !agendaItemId) {
      throw new Error(
        "Failed to delete agenda item: tenantId or agendaItemId is blank."
      );
    }

    const hooks = this.hooks.execute({ tenantId, agendaItemId }, "delete");
    await hooks.before(null);

    await apiService.v1.deleteAgendaItem(tenantId, agendaItemId);

    await hooks.after(null);
    this.cache.publish(agendaItemId, null);
  }

  /** Check if an item is pending. Returns null if the status could not be determined because
   * meetings haven't been loaded. */
  isPending(
    item: AgendaItem,
    meetings?: (Meeting | MeetingDto)[] | null,
    tenantId?: string | null
  ): boolean | null {
    /** Condition:
     *
     * Template items are never pending. Then,
     *
     * For approval items:
     * - The item is pending if it hasn't been approved / rejected, and if it's not in a meeting
     *   that hasn't been run.
     *
     * For non-approval items:
     * - The item is pending if it's not in a meeting. */

    // if an item is a template item, or was approved or rejected, it is *not* pending
    const status = item.review?.status;
    if (item.template || status === "approved" || status === "rejected") {
      return false;
    }
    // if an item is not in any meeting, it *is* pending
    if (!item.parentIds?.length) return true;

    // otherwise, check what meetings it's in
    meetings ??= meetingRepo.listCached(
      tenantId ?? baseRepo.getTenantId() ?? ""
    );

    if (!meetings) return null; // if meetings aren't cached, we don't know

    // check if the item is a 'no approval' item
    const noApproval =
      !item.approver?.approverType || item.approver.approverType === "none";

    // try to find a meeting that wasn't run, that the item is in
    for (const meeting of meetings) {
      // if meeting was run, skip for items requiring approval
      if (!noApproval && meeting.minutesData) continue;

      for (const sectionId of meeting.agenda?.agendaModuleIds ?? []) {
        if (item.parentIds.includes(sectionId)) {
          // item is in this not-run meeting: it is *not* pending
          return false;
        }
      }
    }
    // not-run meeting wasn't found with item: it *is* pending
    return true;
  }

  invalidate(agendaItem: OnlyRequire<AgendaItemDto, "id">) {
    this.cache.invalidateEntry(agendaItem.id);
  }

  invalidateAll() {
    this.cache.clear();
  }

  init(data?: Partial<AgendaItemDto>): AgendaItemDto {
    return baseRepo.init({
      tenantId: baseRepo.getTenantId() || "UNKNOWN",
      status: "draft",
      name: "",
      description: "",
      body: "",
      submittedBy: "",
      type: "",
      documentIds: [],
      documents: [],
      ...data,
    });
  }
}

export const agendaItemRepo = new AgendaItemRepository();

agendaItemRepo.addHook(() => {
  const before = (agendaItem: Partial<AgendaItemDto>) => {
    return { ...agendaItem, documents: undefined };
  };

  return {
    beforeCreate: before,
    beforeUpdate: before,
  };
}, hookPriorities.removeNestedEntities);

agendaItemRepo.addHook(() => {
  const before = (agendaItem: Partial<AgendaItemDto>) => {
    // if an approver is assigned, overwrite the item's team
    if (
      agendaItem.approver?.approverType === "team" &&
      agendaItem.approver.approverId
    ) {
      agendaItem.teamId = agendaItem.approver.approverId;
    }
  };

  return {
    beforeCreate: before,
    beforeUpdate: before,
  };
}, hookPriorities.switchItemTeam);

agendaItemRepo.addHook(({ tenantId }) => {
  const after = (agendaItem: AgendaItemDto) => {
    if (agendaItem.approver?.approverType === "user") {
      // This takes awhile, and response is ignored: run without await.
      apiService.v1.mailItemApproval(tenantId, agendaItem.id);
    }
  };

  return {
    afterCreate: after,
  };
}, hookPriorities.postSaveActions);
